import type { KnowledgeDocument } from "@/entities/knowledge/api/knowledgeService";
import { ApiError } from "@/shared/api";

/**
 * 「AI 客服到底看不看得见这份文档」的唯一判定（2026-08-07 生产事故产物）。
 *
 * 事故形态：某租户传了 19 份知识库文档，管理端显示全部「已完成」，而 AI 客服**一条都检索不到**，
 * 每问必转人工，租户被蒙了两个小时才报障。原因是公开检索要求**两根正交的治理轴同时满足**，
 * 而管理端只显示了其中一根（审核状态），另一根（对客可见性）当时甚至没有字段。
 *
 * 所以这个函数存在的意义不是「少写一个 &&」，而是：把「能不能被客服用」这件事收成**一个**
 * 可断言的判定，而不是散在几个组件里各显示各的、谁也拼不出全貌。
 */
export function isCustomerVisible(
  doc: Pick<KnowledgeDocument, "status" | "reviewStatus" | "visibility">
): boolean {
  return doc.status === "done" && doc.reviewStatus === "approved" && doc.visibility === "public";
}

/** 一份文档「用不上」的具体原因 —— 只说「不可见」等于没说，租户还得自己猜该点哪儿。 */
export type InvisibleReason =
  | "ingesting" // 向量化还没跑完，Qdrant 里还没有它的点
  | "not_approved" // 治理轴：还没人批 / 已驳回
  | "internal_only"; // 治理轴：只对内，客服检索面里没有它

export function invisibleReason(
  doc: Pick<KnowledgeDocument, "status" | "reviewStatus" | "visibility">
): InvisibleReason | null {
  if (isCustomerVisible(doc)) return null;
  if (doc.status !== "done") return "ingesting";
  if (doc.reviewStatus !== "approved") return "not_approved";
  return "internal_only";
}

export const INVISIBLE_REASON_LABEL: Record<InvisibleReason, string> = {
  ingesting: "处理中，客服暂时用不到",
  not_approved: "未审核通过，客服检索不到",
  internal_only: "仅内部可见，客服检索不到",
};

/**
 * 全库口径的「客服可见 N / 共 M 份」。
 *
 * 这一行是本次事故最直接的防线：如果当时列表顶部写着「客服可见 0 / 19 份」，
 * 租户配完第一眼就发现了，根本轮不到等客户问了才报障。比任何筛选器都值钱。
 */
export function summarizeVisibility(
  docs: ReadonlyArray<Pick<KnowledgeDocument, "status" | "reviewStatus" | "visibility">>
): { visible: number; total: number } {
  return {
    visible: docs.filter(isCustomerVisible).length,
    total: docs.length,
  };
}

/** 汇总要不要报警色：一份都不可见 = 客服此刻是哑的，必须显眼。 */
export function isVisibilityAlarming(summary: { visible: number; total: number }): boolean {
  return summary.total > 0 && summary.visible === 0;
}

type VisibilityGateInput = Pick<KnowledgeDocument, "status" | "reviewStatus" | "visibility">;

/**
 * 能不能**放开对客可见**(2026-08-08 补的那个动作)。
 *
 * ★这一步此前根本没有入口:事故修完之后,写侧/闸门/三轴显示全齐了,但 `approve` 只翻
 * `review_status` 这一根,而全站没有任何 UI 传 `visibility="public"` —— 租户自己传的文档
 * 审完了也永远停在 `internal`,客服照样检索不到。列表上「仅内部」的黄标显示得好好的,点不动。
 *
 * 两个前置条件,理由各不相同:
 * - **必须已审**:后端同款口径(「只有已批准的内容才能对客可见」)。这里不是唯一防线,是别把
 *   按钮亮着骗人 —— 点了弹一个 400,比按钮不出现更糟。
 * - **必须向量化跑完**:没跑完时 Qdrant 里还没有这份文档的点,反写翻不到任何东西,等于放开了
 *   个空壳(与 `canApprove` 同一个理由)。
 */
export function canGrantCustomerVisible(doc: VisibilityGateInput): boolean {
  return doc.reviewStatus === "approved" && doc.status === "done" && doc.visibility !== "public";
}

/**
 * 能不能**收回对客可见**。
 *
 * ★与 {@link canGrantCustomerVisible} 刻意不对称:**收回是安全阀,永远赢**(与
 * `canRevokeShareable` 同款口径)。发现一份内容不该对客的那一刻,它可能正处在任何状态
 * (还没审完、向量化失败……)—— 这时要求「先审成 approved 才允许收回」是荒唐的。
 * 放开要过闸,收紧永远放行。
 */
export function canRevokeCustomerVisible(doc: VisibilityGateInput): boolean {
  return doc.visibility === "public";
}

/** 列表可见性筛选的档位。 */
export type VisibilityFilter = "all" | "visible" | "internal_only" | "not_ready";

export const VISIBILITY_FILTER_LABEL: Record<VisibilityFilter, string> = {
  all: "全部",
  visible: "AI 客服可见",
  internal_only: "仅内部",
  not_ready: "未生效(处理中/未审核)",
};

/**
 * 按可见性筛列表。
 *
 * `not_ready` 把「处理中」和「未审核」并成一档:对租户来说它们是同一件事 ——
 * 这份东西客服现在用不上，且原因不在可见性这根轴上。分太细只会让人对着四个筛选项发呆。
 */
export function filterByVisibility<
  T extends Pick<KnowledgeDocument, "status" | "reviewStatus" | "visibility">,
>(docs: ReadonlyArray<T>, filter: VisibilityFilter): T[] {
  if (filter === "all") return [...docs];
  return docs.filter((d) => {
    const reason = invisibleReason(d);
    if (filter === "visible") return reason === null;
    if (filter === "internal_only") return reason === "internal_only";
    return reason === "ingesting" || reason === "not_approved";
  });
}

/**
 * 批量操作只允许**收回**(public → internal),不允许批量放开。
 *
 * 这不是偷懒,是风险不对称:批量收回的失败形态是「客服少答几句」——当天可见、一键放回;
 * 批量放开的失败形态是「内部内容被讲给客户」——可能几个月没人发现,而且话说出去收不回。
 * 2026-08-07 刚被「内容悄悄对客可见」咬过一次,不该顺手再造一个更快的口子。
 *
 * 真要批量放开(比如换一版对客话术库),得单独设计带内容抽样预览的确认流程,不走这里。
 */
export function batchRevocableIds<T extends { id: number; visibility?: string }>(
  docs: ReadonlyArray<T>,
  selectedIds: ReadonlySet<number>
): number[] {
  return docs.filter((d) => selectedIds.has(d.id) && d.visibility === "public").map((d) => d.id);
}

/**
 * 放开对客撞四眼(1025 客服舱录片实录):后端 403「不能审批自己提交的内容」,而通用 toast 写的是
 * 「设置失败(未生效,可重试)· 如需此权限请联系管理员」—— 点的人自己就是管理员,重试也永远不会成,
 * 等于没说该怎么办。这里把这一种失败翻成人话:找另一位管理员 / 平台运营来点。其它失败返回 fallback。
 */
export function visibilityErrorToast(fallback: string) {
  return (err: Error) => {
    if (
      err instanceof ApiError &&
      err.errorCode === "FORBIDDEN" &&
      err.message.includes("自己提交")
    ) {
      return {
        title: "这份需要另一位管理员来放开",
        description:
          "它是你自己提交的。按四眼原则,放开给 AI 客服要由企业里另一位管理员(或平台运营)操作 —— 请对方在「知识库 → 文档列表」点「设为对客可见」。",
        variant: "destructive" as const,
      };
    }
    return fallback;
  };
}
